import {
    Dossier,
    DossierStatus,
    DossierStatusEnum,
    establishementStatusEnum,
    EstablishmentStatus,
    RolesEnum,
} from "../../types";
import { DropDownItem } from "../UIElements";

const adminsReseauRegionStatuses = [
    DossierStatusEnum.CREATION,
    DossierStatusEnum.EN_COURS_D_INSTRUCTION,
];

const clientDossierStatuses = [
    DossierStatusEnum.CREATION,
    DossierStatusEnum.SOUMIS,
    DossierStatusEnum.AFFECTE,
    DossierStatusEnum.EN_COURS_D_INSTRUCTION,
    DossierStatusEnum.ATTENTE_DECISION,
    DossierStatusEnum.FINALISE,
];

const instructorDossierStatuses = [
    DossierStatusEnum.AFFECTE,
    DossierStatusEnum.EN_COURS_D_INSTRUCTION,
    DossierStatusEnum.CREATION,
];

const adminDossierStatuses = [
    DossierStatusEnum.SOUMIS,
    DossierStatusEnum.AFFECTE,
    DossierStatusEnum.CREATION,
    DossierStatusEnum.EN_COURS_D_INSTRUCTION,
    DossierStatusEnum.ATTENTE_DECISION,
    DossierStatusEnum.FINALISE,
];

const synerpaStatuses = [
    DossierStatusEnum.ATTENTE_DECISION,
    DossierStatusEnum.FINALISE,
];

export function filterDossierStatusByRole(
    dossierStatus: DossierStatus[],
    userRoleId: number
) {
    // client

    const getDossierStatusesByUserRole = (userRoleId: number) => {
        switch (userRoleId) {
            case RolesEnum.ADMIN_REGION:
            case RolesEnum.ADMIN_RESEAU:
                return adminsReseauRegionStatuses;
            case RolesEnum.CLIENT:
                return clientDossierStatuses;
            case RolesEnum.ADMIN:
                return adminDossierStatuses;
            case RolesEnum.INSTRUCTEUR:
                return instructorDossierStatuses;
            case RolesEnum.SYNERPA:
                return synerpaStatuses;
            default:
                return [];
        }
    };

    return dossierStatus.filter((d: DossierStatus) => {
        return getDossierStatusesByUserRole(userRoleId).includes(d.code as any);
    });
}

export const getDossierStatusOfDossier = (
    dossier: Dossier,
    dossierStatus: DossierStatus[]
) => {
    return dossierStatus.find(
        (ds) => dossier && ds.id === dossier.dossierStatus
    );
};

export const getDossierStatusByCode = (
    code: DossierStatusEnum[keyof DossierStatusEnum],
    dossierStatus: DossierStatus[]
) => {
    return dossierStatus.find((ds) => ds.code === code);
};

export const getEstablishmentStatusByCode = (
    code: establishementStatusEnum[keyof establishementStatusEnum],
    establishmentStatus: EstablishmentStatus[]
) => {
    return establishmentStatus.find((es) => es.code === code);
};

export const getEstablishmentStatusById = (
    establishementId: number,
    establishmentStatus: EstablishmentStatus[]
) => {
    return establishmentStatus.find((es) => es.id === establishementId);
};

export const getDossierStatusesIdsByUserRole = (
    userRoleId: number,
    dossierStatus: DossierStatus[]
) => {
    switch (userRoleId) {
        case RolesEnum.ADMIN_REGION:
        case RolesEnum.ADMIN_RESEAU:
            const adminsResauRegionDossierStatuseIds: any[] =
                adminsReseauRegionStatuses
                    .map(
                        (s) =>
                            (getDossierStatusByCode(s, dossierStatus) || {}).id
                    )
                    .filter((id) => id !== undefined);
            return adminsResauRegionDossierStatuseIds;
        case RolesEnum.CLIENT:
            const clientDossierStatuseIds: any[] = clientDossierStatuses
                .map((s) => (getDossierStatusByCode(s, dossierStatus) || {}).id)
                .filter((id) => id !== undefined);
            return clientDossierStatuseIds;

        case RolesEnum.ADMIN:
            const adminDossierStatuseIds: any[] = adminDossierStatuses
                .map((s) => (getDossierStatusByCode(s, dossierStatus) || {}).id)
                .filter((id) => id !== undefined);

            return adminDossierStatuseIds;

        case RolesEnum.INSTRUCTEUR:
            const instructorDossierStatuseId: any[] = instructorDossierStatuses
                .map((s) => (getDossierStatusByCode(s, dossierStatus) || {}).id)
                .filter((id) => id !== undefined);
            return instructorDossierStatuseId;
        case RolesEnum.SYNERPA:
            const synerpaStatusesDossierStatuseId: any[] = synerpaStatuses
                .map((s) => (getDossierStatusByCode(s, dossierStatus) || {}).id)
                .filter((id) => id !== undefined);
            return synerpaStatusesDossierStatuseId;
        default:
            return [];
    }
};

export const getLayoutMainHeaderTitleByUserRole = (userRoleId: number) => {
    switch (userRoleId) {
        case RolesEnum.CLIENT:
            return "Mes dossiers de qualifications";
        case RolesEnum.SYNERPA:
        case RolesEnum.ADMIN:
            return "Dossiers";
        case RolesEnum.INSTRUCTEUR:
            return "Mes dossiers affectés";
        case RolesEnum.ADMIN_RESEAU:
            return "Dossiers de mon réseau";
        case RolesEnum.ADMIN_REGION:
            return "Dossiers de ma région";
        default:
            return "";
    }
};

export const getLayoutHeaderTitleByUserRole = (userRoleId: number) => {
    switch (userRoleId) {
        case RolesEnum.CLIENT:
            return "Retrouvez tous vos dossiers de qualifications";
        case RolesEnum.ADMIN:
            return "Gérez les dossiers de qualification à affecter";
        case RolesEnum.INSTRUCTEUR:
            return "Gérez tous les dossiers qui vous ont été affectés";
        case RolesEnum.SYNERPA:
            return "Examinez et rendez une décision sur les dossiers de qualification";
        case RolesEnum.ADMIN_RESEAU:
            return "Gérer les dossiers de votre réseaux";
        case RolesEnum.ADMIN_REGION:
            return "Gérer les dossier de votre région";
        default:
            return "";
    }
};

export const canShowBulkAssignedDossierBtn = (userId: number) => {
    return [RolesEnum.ADMIN].includes(userId);
};

export function sortDossiersByUserRoleId(
    userRoleId: number,
    dossiers: Dossier[]
) {
    switch (userRoleId) {
        case RolesEnum.ADMIN_REGION:
        case RolesEnum.ADMIN_RESEAU:
        case RolesEnum.CLIENT:
            return dossiers;
        case RolesEnum.ADMIN:
            return "Dossiers";
        case RolesEnum.INSTRUCTEUR:
            return "Mes dossiers affectés";
        // case RolesEnum.SYNERPA:
        //     return "";
        default:
            return "";
    }
}

export const buildInstructorDropDownItems = (
    instructorList: DropDownItem[]
): any[] => {
    return instructorList.map((instructor: any) => {
        return {
            id: instructor.id,
            name: instructor.firstName + " " + instructor.lastName,
            name2: `${instructor.dossierQty} dossiers affectés`,
        };
    });
};
